import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Paper, Title, Text, Button, Group, Alert, Modal, Stack, Loader, Center, Badge, Table, SimpleGrid,
  SegmentedControl, Collapse, Divider
} from '@mantine/core';
import { IconAlertCircle, IconCheck, IconArrowLeft, IconLock, IconLockOpen, IconMail } from '@tabler/icons-react';
import useSchool from '../../../hooks/useSchool';
import ConfirmationTable from '../../../components/School/ConfirmationTable';
import { postApi } from '../../../lib/api';
import { CONTACT_EMAIL, getDeadlineLabel } from '../../../config/seasons';

const money = (n) => `$${Number(n).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Line = ({ label, value, strong, color }) => (
  <Group justify="space-between">
    <Text size="sm" fw={strong ? 700 : 400} style={{ textAlign: 'left', color }}>{label}</Text>
    <Text size="sm" fw={strong ? 700 : 500} style={{ color }}>{value}</Text>
  </Group>
);

/**
 * Estimated invoice: entries at regular prices, the school tier discount on
 * the whole order, HST. Plus a per-student breakdown (same % for everyone)
 * the teacher can show with or without the discount.
 */
const EstimatePanel = ({ estimate }) => {
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [view, setView] = useState('discounted');
  if (!estimate || !estimate.breakdown.length) return null;
  const full = view === 'full';

  return (
    <Paper p="xl" radius="md" withBorder mb="xl">
      <Title order={4} mb={4} style={{ color: 'var(--charcoal)' }}>Estimated invoice</Title>
      <Text size="xs" color="dimmed" mb="md" style={{ textAlign: 'left' }}>
        Regular prices; the school discount is based on your whole order. We'll confirm the final amount on your invoice.
      </Text>
      <Stack gap={6} maw={460}>
        <Line label={`Entries (${estimate.breakdown.length} student${estimate.breakdown.length !== 1 ? 's' : ''})`} value={money(estimate.subtotal)} />
        {estimate.tier && (
          <Line label={`${estimate.tier.name} school discount (${estimate.tier.percentOff}%)`} value={`-${money(estimate.discount)}`} />
        )}
        <Line label="HST (13%)" value={money(estimate.hst)} />
        <Divider />
        <Line label="Estimated total" value={money(estimate.total)} strong />
      </Stack>
      {estimate.nextTier && (
        <Text size="sm" mt="md" style={{ textAlign: 'left', color: 'var(--deep-gold)' }}>
          Add {money(estimate.nextTier.amountNeeded)} more in entries to reach {estimate.nextTier.name} ({estimate.nextTier.percentOff}% off your whole order).
        </Text>
      )}

      <Button variant="subtle" color="gold" size="xs" mt="md" px={0} onClick={() => setShowBreakdown(v => !v)}>
        {showBreakdown ? 'Hide per-student breakdown' : 'Per-student breakdown'}
      </Button>
      <Collapse in={showBreakdown}>
        <Group justify="space-between" mt="sm" mb="sm" wrap="wrap" gap="xs">
          <Text size="xs" color="dimmed" style={{ textAlign: 'left' }}>
            What each student's entries come to, if you charge families individually.
          </Text>
          <SegmentedControl
            size="xs"
            value={view}
            onChange={setView}
            data={[{ value: 'discounted', label: 'With discount' }, { value: 'full', label: 'Full price' }]}
          />
        </Group>
        <div style={{ overflowX: 'auto' }}>
          <Table striped withTableBorder fz="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Student</Table.Th>
                <Table.Th>Entries</Table.Th>
                <Table.Th style={{ textAlign: 'right' }}>Before HST</Table.Th>
                <Table.Th style={{ textAlign: 'right' }}>HST</Table.Th>
                <Table.Th style={{ textAlign: 'right' }}>Total</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {estimate.breakdown.map(b => (
                <Table.Tr key={b.registrationId}>
                  <Table.Td>{b.name}</Table.Td>
                  <Table.Td>
                    <Text size="xs">
                      Registration{b.solos ? ` · ${b.solos} solo${b.solos > 1 ? 's' : ''}` : ''}
                      {b.ensembles ? ` · ${b.ensembles} ensemble${b.ensembles > 1 ? 's' : ''}` : ''}
                    </Text>
                  </Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{money(full ? b.fullPrice : b.afterDiscount)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{money(full ? b.fullTotal - b.fullPrice : b.hst)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{money(full ? b.fullTotal : b.total)}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </div>
      </Collapse>
    </Paper>
  );
};

// Big number over a small label, both explicitly centered (the site's global
// styles left-align text otherwise).
const Stat = ({ label, value }) => (
  <Stack gap={2} align="center">
    <Text ta="center" fw={700} style={{ fontSize: '1.75rem', lineHeight: 1.1, color: 'var(--deep-gold)', fontFamily: "'Playfair Display', serif" }}>
      {value}
    </Text>
    <Text ta="center" size="xs" c="dimmed" tt="uppercase" fw={600} style={{ letterSpacing: '0.04em' }}>
      {label}
    </Text>
  </Stack>
);

/**
 * School portal step 4: review & finalize. Everything comes from
 * /api/ensemble (schoolReview), which also decides what blocks finalizing:
 * a student with no entries, a solo without footwear, or a team that breaks
 * the rules. Missing music and piece details can be finished later.
 */
const SchoolConfirmation = ({ readOnly = false }) => {
  const navigate = useNavigate();
  const { school, currentSeason, reopenSeason, reload } = useSchool();
  const seasonId = currentSeason?.id;

  const [review, setReview] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [finalizeModalOpen, setFinalizeModalOpen] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [finalizeError, setFinalizeError] = useState(null);
  const [reopening, setReopening] = useState(false);

  const deadlineLabel = getDeadlineLabel(currentSeason?.competition_year);

  const load = useCallback(() => {
    if (!seasonId) return;
    postApi('/api/ensemble', { action: 'schoolReview', schoolSeasonId: seasonId })
      .then(setReview)
      .catch(err => setLoadError(err.message));
  }, [seasonId]);

  useEffect(() => { load(); }, [load]);

  const handleFinalize = async () => {
    setFinalizing(true);
    setFinalizeError(null);
    try {
      const res = await postApi('/api/ensemble', { action: 'schoolFinalize', schoolSeasonId: seasonId });
      setReview(res);
      setFinalizeModalOpen(false);
      await reload();
    } catch (err) {
      setFinalizeError(err.message);
    } finally {
      setFinalizing(false);
    }
  };

  const handleReopen = async () => {
    setReopening(true);
    const ok = await reopenSeason();
    setReopening(false);
    if (ok) {
      await reload();
      navigate('/register/school/students');
    }
  };

  if (loadError) return <Alert color="red" m="xl">{loadError}</Alert>;
  if (!review) {
    return (
      <Center style={{ minHeight: '60vh' }}>
        <Loader color="var(--primary-gold)" />
      </Center>
    );
  }

  const { students, ensembles, counts, blocking, finishLater } = review;
  const finalized = review.finalized;
  const needsConsent = students.filter(s => s.consentStatus === 'required');
  const canFinalize = !blocking.length && students.length > 0;

  const summary = (
    <Paper p="lg" radius="md" withBorder mb="xl" style={{ background: '#fff9f0', borderColor: 'var(--primary-gold)' }}>
      <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="lg" verticalSpacing="lg">
        <Stat label="Students" value={counts.students} />
        <Stat label="Solos" value={counts.solos} />
        <Stat label="Ensemble spots" value={counts.ensembleEntries} />
        <Stat label="Ensembles" value={counts.ensembles} />
      </SimpleGrid>
    </Paper>
  );

  const ensemblesTable = ensembles.length > 0 && (
    <Paper p="xl" radius="md" withBorder mb="xl">
      <Group justify="space-between" mb="lg">
        <Title order={4} style={{ color: 'var(--charcoal)' }}>Ensembles</Title>
        {!readOnly && !finalized && (
          <Button size="xs" variant="subtle" color="gold" onClick={() => navigate('/register/school/ensembles')}>Edit ensembles</Button>
        )}
      </Group>
      <div style={{ overflowX: 'auto' }}>
        <Table striped withTableBorder withColumnBorders>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Ensemble</Table.Th>
              <Table.Th>Dancers</Table.Th>
              <Table.Th>Division</Table.Th>
              <Table.Th>Piece & music</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {ensembles.map(e => (
              <Table.Tr key={e.id}>
                <Table.Td><Text fw={500}>{e.name}</Text></Table.Td>
                <Table.Td><Text size="xs">{e.members.map(m => m.name).join(', ')}</Text></Table.Td>
                <Table.Td>
                  {e.check.eligible
                    ? <Badge color="green" variant="light">{e.check.division}</Badge>
                    : <Text size="xs" color="#c92a2a">{e.check.problems.join(' ')}</Text>}
                </Table.Td>
                <Table.Td>
                  <Text size="xs">
                    {e.piece_title || <span style={{ color: '#888' }}>Piece not chosen yet</span>}
                    {' · '}
                    {e.music_file_url ? 'Music ✓' : <span style={{ color: '#888' }}>no music yet</span>}
                  </Text>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </div>
    </Paper>
  );

  return (
    <div style={{ background: 'linear-gradient(135deg, #faf9f7 0%, #f5f3f0 100%)', minHeight: '100vh', padding: '2rem 1rem' }}>
      <Container size="lg">

        {finalized ? (
          <>
            <Paper p="xl" radius="md" withBorder mb="xl" style={{ textAlign: 'center', borderTop: '4px solid var(--primary-gold)' }}>
              <div style={{
                width: '80px', height: '80px', borderRadius: '50%',
                background: 'linear-gradient(135deg, var(--primary-gold), var(--deep-gold))',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 1.5rem'
              }}>
                <IconCheck size={40} color="white" />
              </div>
              <Title order={2} mb="sm" style={{ color: 'var(--primary-gold)', fontFamily: "'Playfair Display', serif" }}>
                Registration Submitted!
              </Title>
              <Text color="dimmed" mb="xl" ta="center">
                <strong>{school?.name}</strong> is registered with <strong>{counts.students} student{counts.students !== 1 ? 's' : ''}</strong>,{' '}
                {counts.solos} solo{counts.solos !== 1 ? 's' : ''} and {counts.ensembles} ensemble{counts.ensembles !== 1 ? 's' : ''}.
                We'll be in touch with your invoice.
              </Text>
              {finishLater.length > 0 && (
                <Alert color="blue" variant="light" mb="lg" style={{ textAlign: 'left' }}>
                  <Text size="sm">
                    <strong>Still to finish by {deadlineLabel}:</strong> {finishLater.length} item{finishLater.length !== 1 ? 's' : ''} (music
                    or piece details). Reopen your registration to add them.
                  </Text>
                </Alert>
              )}
              <Group justify="center">
                {!readOnly && (
                  <Button
                    variant="light"
                    color="gold"
                    leftSection={<IconLockOpen size={16} />}
                    onClick={handleReopen}
                    loading={reopening}
                  >
                    Reopen & Edit
                  </Button>
                )}
                {readOnly && (
                  <Button
                    component="a"
                    href={`mailto:${CONTACT_EMAIL}`}
                    leftSection={<IconMail size={16} />}
                    style={{ background: 'linear-gradient(135deg, var(--primary-gold), var(--deep-gold))' }}
                  >
                    Contact us
                  </Button>
                )}
              </Group>
              {!readOnly && (
                <Text size="xs" color="dimmed" mt="md" ta="center">
                  You can still make changes: reopen, edit, and finalize again. Anything added after your first
                  finalize is billed as the difference when registration closes.
                </Text>
              )}
            </Paper>
            {summary}
            <EstimatePanel estimate={review.estimate} />
            <Paper p="xl" radius="md" withBorder mb="xl">
              <Title order={4} mb="lg" style={{ color: 'var(--charcoal)' }}>Students</Title>
              <ConfirmationTable students={students} />
            </Paper>
            {ensemblesTable}
          </>
        ) : (
          <>
            {!readOnly && (
              <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '0.75rem' }}>
                <Button
                  variant="subtle"
                  leftSection={<IconArrowLeft size={16} />}
                  onClick={() => navigate('/register/school/ensembles')}
                  color="gray"
                  size="sm"
                >
                  Back to Ensembles
                </Button>
              </div>
            )}

            <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
              <Title order={2} style={{ color: 'var(--primary-gold)', fontFamily: "'Playfair Display', serif" }}>
                Review & Confirm
              </Title>
              <Text color="dimmed" mt="xs" ta="center">
                Check every student's entries before finalizing.
              </Text>
            </div>

            {summary}

            {blocking.length > 0 && (
              <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light" mb="xl" title="Before you can finalize">
                <Stack gap={4}>
                  {blocking.map(b => <Text key={b} size="sm" style={{ textAlign: 'left' }}>• {b}</Text>)}
                </Stack>
              </Alert>
            )}

            {finishLater.length > 0 && (
              <Alert color="blue" variant="light" mb="xl" title="Can be finished later">
                <Text size="sm" mb="xs" style={{ textAlign: 'left' }}>
                  You can finalize now and add these until registration closes ({deadlineLabel}):
                </Text>
                <Stack gap={2}>
                  {finishLater.map(f => <Text key={f} size="xs" style={{ textAlign: 'left' }}>• {f}</Text>)}
                </Stack>
              </Alert>
            )}

            {needsConsent.length > 0 && (
              <Alert icon={<IconAlertCircle size={16} />} color="orange" variant="light" mb="xl">
                <Text size="sm" style={{ textAlign: 'left' }}>
                  <strong>
                    {needsConsent.length} student{needsConsent.length !== 1 ? 's' : ''} under 18
                    {needsConsent.length !== 1 ? ' have' : ' has'} no parent consent email sent yet.
                  </strong>{' '}
                  Your school waiver commits you to collecting parent/guardian consent for every
                  minor. Go back to Students and use <em>Send Consent Email</em> for:{' '}
                  {needsConsent.map(s => s.name).join(', ')}.
                </Text>
              </Alert>
            )}

            <Paper p="xl" radius="md" withBorder mb="xl">
              <Title order={4} mb="lg" style={{ color: 'var(--charcoal)' }}>Students</Title>
              {!readOnly && (
                <Text size="xs" color="dimmed" mb="md">Click a row to edit that student.</Text>
              )}
              <ConfirmationTable
                students={students}
                onEditStudent={readOnly ? undefined : (id) => navigate(`/register/school/students?openStudent=${id}`)}
              />
            </Paper>

            {ensemblesTable}

            <EstimatePanel estimate={review.estimate} />

            {!readOnly && (
              <Paper p="xl" radius="md" withBorder style={{ borderTop: '4px solid var(--primary-gold)', textAlign: 'center' }}>
                <Title order={5} mb="xs" ta="center">Ready to Finalize?</Title>
                <Text size="sm" color="dimmed" mb="lg" ta="center" maw={620} mx="auto">
                  Finalizing sends your registration to our team, and we'll invoice you for these entries.
                  You can still reopen and make changes until registration closes on <strong>{deadlineLabel}</strong>;
                  anything added later is billed as the difference.
                </Text>
                <Group justify="center">
                  <Button
                    size="lg"
                    leftSection={<IconLock size={18} />}
                    onClick={() => { setFinalizeError(null); setFinalizeModalOpen(true); }}
                    disabled={!canFinalize}
                    style={canFinalize ? { background: 'linear-gradient(135deg, var(--primary-gold), var(--deep-gold))' } : undefined}
                  >
                    Finalize Submission
                  </Button>
                </Group>
                {!canFinalize && (
                  <Text size="xs" color="dimmed" mt="sm" ta="center">
                    {students.length ? 'Fix the items listed above first.' : 'Add students first.'}
                  </Text>
                )}
              </Paper>
            )}
          </>
        )}
      </Container>

      <Modal
        opened={finalizeModalOpen}
        onClose={() => setFinalizeModalOpen(false)}
        title={<Text fw={700} size="lg">Confirm Finalization</Text>}
        centered
      >
        <Stack gap="md">
          {finalizeError && <Alert color="red" p="xs"><Text size="sm">{finalizeError}</Text></Alert>}
          <Text>
            Finalize <strong>{school?.name}</strong> with{' '}
            <strong>{counts.students} student{counts.students !== 1 ? 's' : ''}</strong>, {counts.solos} solo{counts.solos !== 1 ? 's' : ''} and{' '}
            {counts.ensembles} ensemble{counts.ensembles !== 1 ? 's' : ''} ({counts.ensembleEntries} ensemble spot{counts.ensembleEntries !== 1 ? 's' : ''})?
          </Text>
          <Text size="sm" color="dimmed">
            We'll invoice you for these entries. You can reopen to make changes until registration closes.
          </Text>
          <Group justify="flex-end" mt="sm">
            <Button variant="subtle" color="gray" onClick={() => setFinalizeModalOpen(false)}>Cancel</Button>
            <Button
              leftSection={<IconCheck size={16} />}
              loading={finalizing}
              onClick={handleFinalize}
              style={{ background: 'linear-gradient(135deg, var(--primary-gold), var(--deep-gold))' }}
            >
              Yes, Finalize
            </Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
};

export default SchoolConfirmation;
